import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Sun, Moon } from 'lucide-react';
import Button from '../common/Button';
import { useAuthStore } from '../../store/authStore';

const AuthMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark';
  });
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, signOut } = useAuthStore();

  const toggleDarkMode = () => {
    setIsDarkMode(prev => {
      const next = !prev;
      document.documentElement.classList.toggle('dark', next);
      localStorage.setItem('theme', next ? 'dark' : 'light');
      return next;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    setIsOpen(false);
    navigate('/');
  };

  const isAdmin = user?.email === 'taramccann@gmail.com';

  return (
    <div className="relative" ref={menuRef}>
      <Button
        variant="ghost"
        onClick={() => setIsOpen(!isOpen)}
        className={user ? "p-2 rounded-full hover:bg-mist dark:hover:bg-dusk text-ink/70 dark:text-parchment/60" : "px-3 py-1.5 rounded-full hover:bg-mist dark:hover:bg-dusk text-ink/70 dark:text-parchment/60 font-display text-sm"}
        aria-label={user ? 'User menu' : 'Sign In'}
      >
        {user ? <span className="text-sm font-display">Menu</span> : 'Sign In'}
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-52 bg-vellum dark:bg-shadow rounded-xl shadow-lg border border-mist dark:border-dusk py-2 z-50 font-display">
          {user ? (
            <>
              <div className="px-4 py-2.5 text-sm italic text-ink/60 dark:text-parchment/50 border-b border-mist dark:border-dusk">
                {user.email}
              </div>
              <button
                onClick={toggleDarkMode}
                className="w-full text-left px-4 py-2.5 text-ink dark:text-parchment/80 hover:bg-parchment dark:hover:bg-dusk/50 flex items-center transition-colors"
              >
                {isDarkMode ? <Sun size={16} className="mr-2.5 text-sun" /> : <Moon size={16} className="mr-2.5 text-moon" />}
                {isDarkMode ? 'Light Mode' : 'Moon Mode'}
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/profile');
                }}
                className="w-full text-left px-4 py-2.5 text-ink dark:text-parchment/80 hover:bg-parchment dark:hover:bg-dusk/50 transition-colors"
              >
                Profile Settings
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/history');
                }}
                className="w-full text-left px-4 py-2.5 text-ink dark:text-parchment/80 hover:bg-parchment dark:hover:bg-dusk/50 transition-colors"
              >
                Your Readings
              </button>
              {isAdmin && (
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/admin');
                  }}
                  className="w-full text-left px-4 py-2.5 text-ink dark:text-parchment/80 hover:bg-parchment dark:hover:bg-dusk/50 transition-colors"
                >
                  Admin Panel
                </button>
              )}
              <div className="border-t border-mist dark:border-dusk my-1" />
              <button
                onClick={handleSignOut}
                className="w-full text-left px-4 py-2.5 text-sun dark:text-ember hover:bg-parchment dark:hover:bg-dusk/50 flex items-center transition-colors"
              >
                <LogOut size={16} className="mr-2.5" />
                Sign Out
              </button>
            </>
          ) : (
            <>
              <button
                onClick={toggleDarkMode}
                className="w-full text-left px-4 py-2.5 text-ink dark:text-parchment/80 hover:bg-parchment dark:hover:bg-dusk/50 flex items-center transition-colors border-b border-mist dark:border-dusk"
              >
                {isDarkMode ? <Sun size={16} className="mr-2.5 text-sun" /> : <Moon size={16} className="mr-2.5 text-moon" />}
                {isDarkMode ? 'Light Mode' : 'Moon Mode'}
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/login');
                }}
                className="w-full text-left px-4 py-2.5 text-ink dark:text-parchment/80 hover:bg-parchment dark:hover:bg-dusk/50 transition-colors"
              >
                Log In
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/signup');
                }}
                className="w-full text-left px-4 py-2.5 text-ink dark:text-parchment/80 hover:bg-parchment dark:hover:bg-dusk/50 transition-colors"
              >
                Sign Up
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AuthMenu;